import { useRef } from "react";

import { drawersActions, useDispatchDrawers, useDrawers } from "contexts/drawers";

import { WindowContentOnlyProvider } from "../windowRenderMode";
import { DrawerComponent } from "./Drawer";

/**
 * Drawer Container
 *
 * Keeps the last-opened drawer mounted even when closed so that panel state
 * (scroll position, selections, etc.) survives collapse and reopen.
 * Only one drawer can be open at a time.
 *
 * The last-open ID is stored (not the config) and resolved from the registry each render,
 * so unregistered drawers (e.g. SDK removeWindow) are never kept mounted.
 */
export function DrawerContainer() {
    const { openDrawer: openDrawerId, drawerRegistry } = useDrawers();
    const dispatch = useDispatchDrawers();
    const lastOpenIdRef = useRef<string | null>(null);

    if (openDrawerId) {
        lastOpenIdRef.current = openDrawerId;
    }

    const handleClose = () => {
        dispatch(drawersActions.closeDrawer());
    };

    const idToRender = openDrawerId ?? lastOpenIdRef.current;
    const drawerToRender = idToRender ? (drawerRegistry[idToRender] ?? null) : null;

    if (!drawerToRender) {
        return null;
    }

    const DrawerContent = drawerToRender.component;

    return (
        <DrawerComponent
            title={drawerToRender.title}
            icon={drawerToRender.icon}
            isOpen={!!openDrawerId}
            onClose={handleClose}
            hideHeader={drawerToRender.hideHeader}
            data-testid={`drawer-${drawerToRender.id}`}
        >
            <WindowContentOnlyProvider>
                <DrawerContent onClose={handleClose} />
            </WindowContentOnlyProvider>
        </DrawerComponent>
    );
}
